/**
 * İstemci tarafı hata kuyruğu ve raporlama sistemi (G41).
 * window.onerror, unhandledrejection ve 5xx/ağ hatalarını toplar.
 * - Aynı parmak izi dakikada en çok 1 kez kuyruğa girer.
 * - 5 saniyede bir toplu gönderilir (en çok 20 olay).
 * - Sayfa kapanırken sendBeacon / keepalive ile iletilir.
 * - Tüm veriler gönderilmeden önce maskelenir.
 */
import type { ClientErrorInput } from "@halkora/api-types";
import { API_BASE } from "../api/client.ts";
import { maskError, maskText } from "./masking.ts";

const REPORT_INTERVAL_MS = 5_000;
const DEDUP_WINDOW_MS = 60_000;
const MAX_BATCH_SIZE = 20;

const recentFingerprints = new Map<string, number>();
let errorQueue: ClientErrorInput[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let initialized = false;

/** Deterministik 32-bit FNV-1a parmak izi üretir. */
export function computeFingerprint(msg: string, stack?: string): string {
  const norm = msg.replace(/\d+/g, "{N}").replace(/\s+/g, " ").trim().toLocaleLowerCase("tr");
  const firstFrame = stack
    ? (stack
        .split("\n")
        .find((l) => (l.includes("src/") || l.includes("apps/web")) && !l.includes("errorReporter")) ?? "")
    : "";
  const combined = `${norm}|${firstFrame.trim()}`;

  let hash = 0x811c9dc5;
  for (let i = 0; i < combined.length; i++) {
    hash ^= combined.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/** Parmak izinin son 1 dakika içinde gönderilip gönderilmediğini kontrol eder. */
export function shouldReport(fingerprint: string, now = Date.now()): boolean {
  const lastTime = recentFingerprints.get(fingerprint);
  if (lastTime && now - lastTime < DEDUP_WINDOW_MS) {
    return false;
  }
  recentFingerprints.set(fingerprint, now);
  // Bayat kayıtları temizle
  if (recentFingerprints.size > 200) {
    for (const [k, v] of recentFingerprints.entries()) {
      if (now - v > DEDUP_WINDOW_MS) {
        recentFingerprints.delete(k);
      }
    }
  }
  return true;
}

/** Kuyruktaki hataları sunucuya gönderir. */
export async function flushQueue(): Promise<void> {
  if (errorQueue.length === 0) return;

  const batch = errorQueue.slice(0, MAX_BATCH_SIZE);
  errorQueue = errorQueue.slice(MAX_BATCH_SIZE);

  const payload = JSON.stringify({ events: batch });
  const url = `${API_BASE}/v1/client-errors`;

  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      credentials: "include",
    });
  } catch {
    // Raporlama başarısızsa döngüye girmemek için yok say
  }
}

/** Sayfa kapanırken bekleyen hataları sendBeacon veya fetch keepalive ile gönderir. */
export function flushBeacon(): void {
  if (errorQueue.length === 0) return;

  const batch = errorQueue.slice(0, MAX_BATCH_SIZE);
  errorQueue = [];
  const payload = JSON.stringify({ events: batch });
  const url = `${API_BASE}/v1/client-errors`;

  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: "application/json" });
    const ok = navigator.sendBeacon(url, blob);
    if (ok) return;
  }

  // sendBeacon başarısız veya yoksa fetch keepalive
  try {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      credentials: "include",
      keepalive: true,
    }).catch(() => {
      /* yok say */
    });
  } catch {
    /* yok say */
  }
}

import { isChunkLoadError, handleChunkError } from "./chunkRecovery.ts";

let isEnqueuing = false;

/** Hatayı kuyruğa ekler (maskeli, tekilleştirilmiş). */
export function enqueueClientError(input: {
  message: string;
  code?: string;
  stack?: string;
  route?: string;
  requestId?: string;
  extra?: Record<string, unknown>;
}): boolean {
  if (isChunkLoadError(input.message) || (input.stack && isChunkLoadError(input.stack))) {
    handleChunkError(input.message);
    return false;
  }
  if (isEnqueuing) {
    return false;
  }
  isEnqueuing = true;
  try {
    const masked = maskError({ message: input.message, stack: input.stack });
    const fp = computeFingerprint(masked.message, masked.stack);

    if (!shouldReport(fp)) {
      return false;
    }

    const sanitizedExtra: Record<string, unknown> = {};
    if (input.extra) {
      for (const [k, v] of Object.entries(input.extra)) {
        if (typeof v === "string") {
          sanitizedExtra[k] = maskText(v);
        } else {
          sanitizedExtra[k] = v;
        }
      }
    }

    const event: ClientErrorInput = {
      message: masked.message,
      code: input.code ?? "CLIENT_ERROR",
      stack: masked.stack,
      route: typeof window !== "undefined" ? window.location.pathname : input.route,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      request_id: input.requestId,
      extra: Object.keys(sanitizedExtra).length > 0 ? sanitizedExtra : undefined,
    };

    errorQueue.push(event);

    if (errorQueue.length >= MAX_BATCH_SIZE) {
      void flushQueue();
    }

    return true;
  } catch {
    return false;
  } finally {
    isEnqueuing = false;
  }
}

/** Genel yakalayıcıları ve periyodik kuyruk boşaltmayı başlatır. */
export function initErrorReporter(): () => void {
  if (initialized || typeof window === "undefined") {
    return () => {};
  }
  initialized = true;

  timer = setInterval(() => {
    void flushQueue();
  }, REPORT_INTERVAL_MS);

  const handleError = (event: ErrorEvent) => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
      handleChunkError(event.error ?? event.message);
      return;
    }
    enqueueClientError({
      message: event.message || "Bilinmeyen betik hatası",
      stack: event.error?.stack,
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  };

  const handleRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      handleChunkError(event.reason);
      return;
    }
    try {
      const reason = event.reason;
      const masked = maskError(reason);
      enqueueClientError({
        message: `İşlenmemiş Promise reddi: ${masked.message}`,
        stack: masked.stack,
        extra: {
          type: "unhandledrejection",
        },
      });
    } catch {
      /* yok say */
    }
  };

  const handleUnload = () => {
    flushBeacon();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      flushBeacon();
    }
  };

  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", handleRejection);
  window.addEventListener("beforeunload", handleUnload);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    if (timer) clearInterval(timer);
    window.removeEventListener("error", handleError);
    window.removeEventListener("unhandledrejection", handleRejection);
    window.removeEventListener("beforeunload", handleUnload);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    initialized = false;
  };
}

/** Testler için kuyruk durumunu sıfırlar. */
export function _resetQueueForTesting(): void {
  errorQueue = [];
  recentFingerprints.clear();
}

/** Testler için kuyruktaki mevcut olayları döner. */
export function _getQueueForTesting(): ClientErrorInput[] {
  return [...errorQueue];
}
